import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';

import apiProducts from 'api/products';
import translations from 'services/translations/translations';

// Whether the rows show the icon of their product image; remembered in the browser
const STORAGE_KEY = 'calculation-window-product-images';

export const ProductImagesContext = createContext(false);

export const useProductImagesShown = (): [
  boolean,
  (shown: boolean) => void
] => {
  const [shown, setShown] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const set = (value: boolean) => {
    setShown(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      // Shown for this time only
    }
  };

  return [shown, set];
};

/**
 * The icon of the product image after the product of a row, when the images are shown and
 * the product has one. Only the id of the image comes with the position: the picture is
 * read when the icon is clicked.
 */
export const ProductImageButton = ({
  imageFileId,
  name,
}: {
  imageFileId?: null | string;
  name: string;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.calculationWindow]);
  const shown = useContext(ProductImagesContext);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<null | string>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || !imageFileId || url) {
      return;
    }

    apiProducts.files
      .get(imageFileId)
      .then((response: { data: BlobPart; headers: Record<string, string> }) =>
        setUrl(
          URL.createObjectURL(
            new Blob([response.data], {
              type: response.headers['content-type'],
            })
          )
        )
      )
      .catch(() => setFailed(true));
  }, [imageFileId, open, url]);

  // The picture read is let go with the row
  useEffect(
    () => () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    },
    [url]
  );

  if (!shown || !imageFileId) {
    return null;
  }

  const title = t(`${translations.calculationWindow}:productImages.Open`);

  return (
    <>
      <button
        aria-label={title}
        className="material-action product-image-action"
        onClick={() => setOpen(true)}
        title={title}
        type="button"
      >
        🖼
      </button>
      <Modal isOpen={open} toggle={() => setOpen(false)} centered>
        <ModalHeader toggle={() => setOpen(false)}>{name}</ModalHeader>
        <ModalBody className="product-image-modal">
          {url ? <img alt={name} src={url} /> : null}
          {!url && !failed ? (
            <p>
              {t(`${translations.calculationWindow}:productImages.Loading`)}
            </p>
          ) : null}
          {failed ? (
            <p>{t(`${translations.calculationWindow}:productImages.Failed`)}</p>
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
};
